import {
  AHSeparator,
  AHText,
  Box,
  Circle,
  TouchableBox,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { RangeSlider } from "@react-native-assets/slider";
import Color from "color";
import { useWeekdays } from "../../../../common/useWeekdays";
import {
  isAllEvenWeeks,
  isAllOddWeeks,
  isAllWeekNumbers,
  WeekNumberPickerDialog,
} from "./WeekNumberPickerDialog";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

export function RoutineEditScheduleWeeklyOptions(props: {
  start: number;
  end: number;
  setRange(_: { start: number; end: number }): void;
  weeks: number[];
  setWeeks(_: number[]): void;
}) {
  const theme = useTheme();
  const layout = useLayoutContext();
  const weekdays = useWeekdays("narrow");
  const trackHeight = layout.circle.medium;
  const trackColor = Color(theme["status-active"]).lighten(0.6).toString();

  const customThumb = useCallback(
    ({ value }: { value: number }) => (
      <Circle size="medium" color="status-active">
        <AHText type="subtitle" color="text-inverted">
          {weekdays[value]}
        </AHText>
      </Circle>
    ),
    [weekdays]
  );

  const customMark = useCallback(
    ({ value }: { value: number }) => {
      return (
        <Circle
          size="medium"
          style={{
            backgroundColor:
              value >= props.start && value <= props.end
                ? "transparent"
                : theme["surface-backgroundControl"],
          }}
        >
          <AHText
            type="subtitle"
            color={
              value >= props.start && value <= props.end
                ? "text-inverted"
                : "text-secondary"
            }
          >
            {weekdays[value]}
          </AHText>
        </Circle>
      );
    },
    [props.end, props.start, theme, weekdays]
  );

  const onValueChange = useCallback(
    (range: number[]) => {
      const end = range[0] > range[1] ? range[0] : range[1];
      const start = range[0] > range[1] ? range[1] : range[0];
      props.setRange({ start, end });
      return true;
    },
    [props]
  );

  const [visible, setVisible] = useState(false);
  const intl_tokens = useIntlTokens();
  return (
    <>
      <Box spaceHorizontal="medium" spaceVertical="medium">
        <AHText type="detail" spaceBottom="medium">
          <FormattedMessage
            defaultMessage="Range:"
            description="RoutineEditScheduleWeekly range title"
          />
        </AHText>

        <RangeSlider
          style={{
            height: trackHeight,
            paddingHorizontal: layout.space.medium,
          }}
          minimumRange={0}
          minimumValue={0}
          maximumValue={6}
          step={1}
          crossingAllowed
          range={[props.start, props.end]}
          onValueChange={onValueChange}
          trackHeight={trackHeight}
          slideOnTap
          trackStyle={{
            borderRadius: 0,
            backgroundColor: "transparent",
          }}
          midTrackStyle={{
            backgroundColor: trackColor,
          }}
          CustomThumb={customThumb}
          CustomMark={customMark}
        />
      </Box>
      <AHSeparator spaceHorizontal="medium" />
      <TouchableBox
        spaceVertical="medium"
        style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
        onPress={() => setVisible(true)}
      >
        <AHText type="detail" spaceLeft="medium">
          <FormattedMessage
            defaultMessage="Weeks"
            description="RoutineEditScheduleWeeklyOptions weeks title"
          />
          :
        </AHText>
        <AHText type="subtitle" spaceLeft="small" color="primary">
          {isAllWeekNumbers(props.weeks) ? (
            intl_tokens.All
          ) : isAllOddWeeks(props.weeks) ? (
            intl_tokens.Odd
          ) : isAllEvenWeeks(props.weeks) ? (
            intl_tokens.Even
          ) : (
            <FormattedMessage
              defaultMessage="Custom"
              description="RoutineEditScheduleWeeklyOptions custom weeks label"
            />
          )}
        </AHText>
      </TouchableBox>
      <WeekNumberPickerDialog
        setWeeks={props.setWeeks}
        weeks={props.weeks}
        visible={visible}
        onClose={() => setVisible(false)}
      />
    </>
  );
}
